// Floating Action Button component with Portal rendering
// CREATED: Reusable FAB component that renders via Portal to avoid z-index/transform issues
// Features:
// - React Portal rendering for proper fixed positioning
// - Configurable position, size, and gradient colors
// - Framer Motion animations with reduced motion support
// - Accessible with ARIA labels and keyboard support
// - Positioned above bottom nav (z-index 85) but below modals

import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { getPortalRoot } from '../../lib/portalUtils';
import { Z_FAB } from '../../lib/zIndex';

type FABSize = 'sm' | 'md' | 'lg';

interface FloatingActionButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  ariaLabel: string;
  title?: string;
  position?: string;
  className?: string;
  gradientFrom?: string;
  gradientTo?: string;
  size?: FABSize;
}

const sizeClasses = {
  sm: 'w-12 h-12',
  md: 'w-14 h-14',
  lg: 'w-16 h-16',
};

const iconSizes = {
  sm: 20,
  md: 24,
  lg: 28,
};

export function FloatingActionButton({
  onClick,
  icon,
  ariaLabel,
  title,
  position = 'bottom-20 right-6',
  className = '',
  gradientFrom = 'primary-600',
  gradientTo = 'primary-700',
  size = 'md',
}: FloatingActionButtonProps) {
  const shouldReduceMotion = useReducedMotion();

  const animationVariants = shouldReduceMotion
    ? {}
    : {
        initial: { scale: 0, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        whileHover: { scale: 1.05 },
        whileTap: { scale: 0.95 },
      };

  const transitionConfig = shouldReduceMotion
    ? { duration: 0 }
    : {
        type: 'spring',
        stiffness: 260,
        damping: 20,
      };

  const button = (
    <motion.button
      onClick={onClick}
      className={`
        fixed ${position} ${sizeClasses[size]}
        flex items-center justify-center
        bg-gradient-to-r from-${gradientFrom} to-${gradientTo}
        hover:from-${gradientFrom.replace('500', '600')} hover:to-${gradientTo.replace('600', '700')}
        text-white rounded-full shadow-lg hover:shadow-xl
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${gradientFrom}
        ${className}
      `}
      style={{ zIndex: Z_FAB }}
      aria-label={ariaLabel}
      title={title}
      role="button"
      {...animationVariants}
      transition={transitionConfig}
    >
      {icon}
    </motion.button>
  );

  return createPortal(button, getPortalRoot());
}
